<script setup>
import { ref } from "vue";
import { useMutation } from "@tanstack/vue-query";
import axios from "axios";
import { errorToast, successToast } from "@/composables/useToasts";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";

const props = defineProps({
  websiteId: {
    type: Number,
    required: true,
  },
  optimiserEnabled: {
    type: Boolean,
    required: true,
  },
  optimiserManuallyEnabled: {
    type: Boolean,
    required: true,
  },
});

const showConfirmModal = ref(false);

const { mutate: invalidateCache, isPending } = useMutation({
  mutationFn: () => axios.post(`/onsite-optimizer/website/${props.websiteId}/invalidate-cache`),
  onSuccess: () => {
    showConfirmModal.value = false;
    successToast('Cache purged successfully.');
  },
  onError: () => {
    showConfirmModal.value = false;
    errorToast('Something went wrong purging the cache. Please try again.');
  },
});
</script>

<template>
  <div class="row">
    <div class="col-md-4">
      <p class="mrg-top-10 xs-mb-0 text-dark">
        <b>Onsite Optimizer</b>
      </p>
    </div>
    <div class="col-md-6">
      <p class="xs-mb-5">
        <strong>Installation status: </strong>
        <span :class="optimiserEnabled ? 'installed' : 'not-installed'">
          {{ optimiserEnabled ? 'Installed' : 'Not Installed' }}
        </span>
      </p>
      <p class="xs-mb-0">
        <a
          :href="`/admin/website/${websiteId}/onsite-optimizer/manage-changes`"
          class="btn btn-sm btn-default btn-icon xs-mt-5 tooltipster"
          title="Manage changes made by Onsite Optimizer"
        >
          <i class="fad fa-cog" /> Manage Changes
        </a>
      </p>
      <p class="xs-mb-5">
        <strong>Manually enabled status: </strong>
        <span :class="optimiserManuallyEnabled ? 'installed' : 'not-installed'">
          {{ optimiserManuallyEnabled ? 'Enabled' : 'Not Enabled' }}
        </span>
      </p>
      <p class="xs-mb-0">
        <a
          :href="`/admin/website/${websiteId}/onsite-optimizer/toggle-manual-optimiser-installation`"
          class="btn btn-sm btn-default btn-icon xs-mt-5 tooltipster"
          title="Manually enable optimizer if Crawler is not picking up the script"
        >
          <i class="fad fa-cog" /> Toggle Manual optimizer installation
        </a>
      </p>
      <p class="xs-mb-5">
        <strong>Purge OSO Cache</strong>
      </p>
      <p class="xs-mb-0">
        <button
          class="btn btn-sm btn-default btn-icon xs-mt-5 tooltipster"
          title="Purge the Fastly cache for this website"
          @click="showConfirmModal = true"
        >
          <i class="fad fa-trash" /> Purge Cache
        </button>
      </p>
    </div>
  </div>

  <Teleport to=".tailwind">
    <BaseModal
      v-if="showConfirmModal"
      :modal-class="'tw-max-w-[500px]'"
      @close="showConfirmModal = false"
    >
      <h2 class="tw-text-2xl tw-text-center tw-font-bold">
        Purge OSO Cache?
      </h2>
      <div>
        <p class="tw-my-6 tw-text-center">
          This will immediately invalidate the Fastly cache for this website's Onsite Optimizer.
          Any cached OSO assets will be purged and re-fetched on the next page load.
          Are you happy to continue?
        </p>
      </div>
      <div class="tw-mt-4 tw-flex tw-justify-center tw-gap-2">
        <DefaultButton
          variant="primary-inverted"
          @click="showConfirmModal = false"
        >
          Cancel
        </DefaultButton>
        <DefaultButton
          variant="danger"
          :loading="isPending"
          :disabled="isPending"
          @click="invalidateCache()"
        >
          {{ isPending ? 'Purging…' : 'Purge Cache' }}
        </DefaultButton>
      </div>
    </BaseModal>
  </Teleport>
</template>
